import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, IconButton, SvgIcon, Typography, useTheme } from "@mui/material";
import { AssetTree } from "../asset-tree";
import { AssetMenuPopover } from "./asset-menu-popover";
import { AssetFilterView } from "../asset-filter-view";
import { usePopover } from "@hooks/shared/use-popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsFilter } from "@fortawesome/pro-solid-svg-icons";
import useAssetStore from "@stores/client/useAssetStore";
import useAssetTreeStore from "@stores/client/useAssetTreeStore";
import useZonesStore from "@stores/client/useZonesStore";

export const AssetMenu = () => {
  const popover = usePopover();
  const theme = useTheme();
  const { t } = useTranslation();
  const params = useParams();
  const { handleAssetFilter, currentFilter } = useAssetStore();
  const { initializeAssetTree } = useAssetTreeStore();
  const { zones } = useZonesStore();

  // Show the current zone's name beneath the "Assets" label so the sidebar
  // says where you are. Matches the zone by its `assets/:rootId` url and
  const currentZone = zones?.find(
    (zone) => zone.url === `assets/${params.rootId}`,
  );
  const zoneTitle = currentZone?.name;

  useEffect(() => {
    const initializeAndFetch = async () => {
      await initializeAssetTree(params.rootId);
    };
    initializeAndFetch();
  }, [params.rootId, initializeAssetTree]);
  return (
    <Box
      sx={{
        width: "100%",
        pb: 5,
        pl: 3,
        pr: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          // Top padding lives on the sticky header (not the scrolling parent)
          // so the gap above it persists while stuck instead of scrolling away.
          pt: 3.5,
          pb: 1.5,
          mb: 1.5,
          backgroundColor: theme.palette.background.default,
          zIndex: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          data-testid="asset-menu-title"
          sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
        >
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "text.secondary",
              lineHeight: 1,
            }}
          >
            {t("client.assets")}
          </Typography>
          {zoneTitle && (
            <Typography
              sx={{
                fontSize: "1.0625rem",
                fontWeight: 700,
                color: "text.primary",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {zoneTitle}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={() => {
            document.activeElement?.blur();
            popover.handleOpen();
          }}
          ref={popover.anchorRef}
          sx={{ flexShrink: 0 }}
          data-testid="asset-menu-filter-icon"
        >
          <SvgIcon fontSize="small" sx={{ color: theme.palette.brandText }}>
            <FontAwesomeIcon icon={faBarsFilter} />
          </SvgIcon>
        </IconButton>
        <AssetMenuPopover
          anchorEl={popover.anchorRef.current}
          onClose={popover.handleClose}
          open={popover.open}
          onFilter={handleAssetFilter}
        />
      </Box>
      {currentFilter === "all" || currentFilter === undefined ? (
        <AssetTree />
      ) : (
        <AssetFilterView />
      )}
    </Box>
  );
};
